<script lang="ts">
  /**
   * Visningsläge för en runda (S-04-25).
   *
   * Read-only modal som visar rundans geometri på en map-preview plus
   * metadata (prioritet, aktiv-fönster, skapad-tid, koord-antal, längd).
   * Ingen edit i denna vy — admin klickar 'Redigera' i footern för att
   * öppna det existerande edit-flödet (HearthsView-mode 'edit').
   *
   * Adresserar Jonas-feedback från live-test 2026-05-05: "man borde
   * kunna se sin runda även fast man inte kan redigera den".
   *
   * Hard-rules:
   *  - Ingen ny datakategori — alla fält kommer från existerande
   *    HearthRecord (S-04-13).
   *  - Inga fritextfält. Backdrop-klick + Esc + 'Stäng' stänger modalen
   *    eftersom det inte finns säkerhetskritisk data att skydda
   *    (modalen lägger inte fram engångskoder eller liknande).
   *  - Användartexter på svenska. Designsystem-tokens — inga nya hex.
   */
  import { isActive, priorityLabel, type HearthRecord } from './hearthsClient';
  import { formatDate } from '../util/format';
  import { parseMultiLineStringWkt } from '../util/wkt';
  import { asRouteDisplayColor, labelForColor } from '../util/colorPalette';
  import RouteMapPreview from './RouteMapPreview.svelte';

  interface Props {
    record: HearthRecord;
    /* eslint-disable no-unused-vars */
    onEdit: (record: HearthRecord) => void;
    onClose: () => void;
    /* eslint-enable no-unused-vars */
  }

  const { record, onEdit, onClose }: Props = $props();

  const polylines: Array<Array<[number, number]>> = $derived(
    parseMultiLineStringWkt(record.geometryWkt),
  );
  const pointCount: number = $derived(polylines.reduce((sum, line) => sum + line.length, 0));
  const totalDistanceMeters: number = $derived(computeTotalDistance(polylines));
  const active: boolean = $derived(isActive(record));

  // S-04-29: snap-status-heuristik. Backend lagrar inte exakt
  // snap-method ännu, så vi avgör klient-side från koord-densitet.
  // GraphHopper-snap genererar typiskt en punkt var 5-10m längs
  // vägnätet. Raka linjer mellan klick har bara klick-punkterna —
  // mycket gles densitet (oftast >> 12m/koord). Tröskeln 12m/koord
  // är konservativ — riktiga snap har vanligen <8m/koord.
  type SnapStatus = 'snapped' | 'fallback' | 'unknown';
  const snapStatus: SnapStatus = $derived(deriveSnapStatus(pointCount, totalDistanceMeters));

  function deriveSnapStatus(points: number, meters: number): SnapStatus {
    if (points < 2 || meters <= 0) return 'unknown';
    const metersPerPoint = meters / Math.max(1, points - 1);
    return metersPerPoint < 12 ? 'snapped' : 'fallback';
  }

  function snapStatusLabel(s: SnapStatus): string {
    if (s === 'snapped') return 'Snappad till väg';
    if (s === 'fallback') return 'Raka linjer mellan klick';
    return 'Okänd';
  }

  function haversineMeters(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number {
    const R = 6_371_000;
    const toRad = (deg: number): number => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  function computeTotalDistance(lines: ReadonlyArray<ReadonlyArray<[number, number]>>): number {
    let total = 0;
    for (const line of lines) {
      for (let i = 1; i < line.length; i += 1) {
        const [lng1, lat1] = line[i - 1];
        const [lng2, lat2] = line[i];
        total += haversineMeters(lat1, lng1, lat2, lng2);
      }
    }
    return total;
  }

  function formatDistance(meters: number): string {
    if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
    return `${Math.round(meters)} m`;
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }

  function handleBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }

  function handleEditClick(): void {
    onEdit(record);
  }
</script>

<svelte:window onkeydown={handleKeyDown} />

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div class="modal-backdrop" role="presentation" onclick={handleBackdropClick}>
  <div
    class="modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="view-rubrik"
    tabindex="-1"
  >
    <header class="modal-header">
      <div class="title-row">
        <h3 id="view-rubrik">{record.name}</h3>
        <span class="status-badge" class:status-active={active} class:status-inactive={!active}>
          {active ? 'Aktiv' : 'Inaktiv'}
        </span>
      </div>
    </header>

    <div class="map-section">
      <RouteMapPreview
        geometryWkt={record.geometryWkt}
        priority={record.priority}
        displayColor={record.displayColor}
      />
    </div>

    <dl class="metadata-grid">
      <div class="metadata-row">
        <dt>Prioritet</dt>
        <dd>{priorityLabel(record.priority)}</dd>
      </div>
      <div class="metadata-row">
        <dt>Färg</dt>
        <dd>
          {#if asRouteDisplayColor(record.displayColor)}
            {labelForColor(asRouteDisplayColor(record.displayColor)!)}
          {:else}
            Prioritetsfärg (fallback)
          {/if}
        </dd>
      </div>
      <div class="metadata-row">
        <dt>
          <span title="Beräknat från koord-tätheten — exakt status sparas inte i databasen i denna version. < 12m mellan koordinater = snappad, >= 12m = raka linjer.">
            Sträckning
          </span>
        </dt>
        <dd
          class="snap-status snap-status-{snapStatus}"
          title="Heuristisk klient-side-beräkning från koord-tätheten"
        >
          {snapStatusLabel(snapStatus)}
        </dd>
      </div>
      <div class="metadata-row">
        <dt>Aktiv från</dt>
        <dd>{formatDate(record.activeFrom)}</dd>
      </div>
      <div class="metadata-row">
        <dt>Aktiv till</dt>
        <dd>{record.activeTo ? formatDate(record.activeTo) : 'Tills vidare'}</dd>
      </div>
      <div class="metadata-row">
        <dt>Skapad</dt>
        <dd>{formatDate(record.createdAt)}</dd>
      </div>
      <div class="metadata-row">
        <dt>Senast uppdaterad</dt>
        <dd>{formatDate(record.updatedAt)}</dd>
      </div>
      <div class="metadata-row">
        <dt>Koordinat-punkter</dt>
        <dd>{pointCount}</dd>
      </div>
      <div class="metadata-row">
        <dt>Längd</dt>
        <dd>{formatDistance(totalDistanceMeters)}</dd>
      </div>
    </dl>

    <footer class="modal-actions">
      <button type="button" class="btn secondary" onclick={onClose}>Stäng</button>
      <button type="button" class="btn primary" onclick={handleEditClick}>Redigera</button>
    </footer>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    display: grid;
    place-items: center;
    padding: 1rem;
    z-index: 50;
  }
  .modal {
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1.25rem;
    width: 100%;
    max-width: 720px;
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
    display: grid;
    gap: 1rem;
  }
  .modal-header {
    display: grid;
    gap: 0.4rem;
  }
  .title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .title-row h3 {
    margin: 0;
    font-size: 1.4rem;
    color: var(--text);
  }
  .status-badge {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    border: 1px solid currentColor;
    font-weight: 600;
  }
  .status-active {
    color: var(--glow);
  }
  .status-inactive {
    color: var(--text-dim);
  }

  .map-section {
    width: 100%;
  }

  .metadata-grid {
    display: grid;
    gap: 0.5rem;
    margin: 0;
    padding: 0.85rem 1rem;
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 4px;
  }
  .metadata-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .metadata-row dt {
    color: var(--text-dim);
    font-size: 0.85rem;
  }
  .metadata-row dd {
    margin: 0;
    color: var(--text);
    font-size: 0.9rem;
    font-weight: 500;
  }
  /* S-04-29: visuell distinktion mellan snappad (positiv) och fallback
     (varning) så admin direkt ser om en runda har god vägkoppling. */
  .snap-status-snapped {
    color: var(--glow);
  }
  .snap-status-fallback {
    color: var(--accent);
    font-style: italic;
  }
  .snap-status-unknown {
    color: var(--text-dim);
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    flex-wrap: wrap;
  }

  .btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 0.55rem 1.1rem;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
  }
  .btn.primary {
    background: var(--accent);
    color: var(--bg);
    border-color: var(--accent);
  }
  .btn.primary:hover:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .btn.secondary {
    background: transparent;
    color: var(--accent);
    border-color: var(--accent);
  }
  .btn.secondary:hover:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }
  .btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
</style>
